import * as THREE from 'three';
import {
  DEBUG_ENABLED,
  visualizeCurveSegments,
  logSegmentStatistics,
  clearDebugVisualization
} from './curveDebugVisualizer.js';

/**
 * Curve detection and fixing utilities for top mesh generation
 * Detects problematic concave/vertical curves in boundary loops and closes them
 * to create a boundary suitable for vertical projection
 */

/**
 * Analyze boundary curvature using face normal + edge direction approach
 * This method checks how each boundary edge faces relative to the mesh surface
 * @param {Array} boundaryLoop - Array of vertex indices forming the boundary
 * @param {Float32Array} positions - Position attribute array
 * @param {Float32Array} normals - Normal attribute array (vertex normals)
 * @returns {Array} Array of curvature analysis objects per vertex
 */
export function analyzeBoundaryCurvature(boundaryLoop, positions, normals) {
  if (!boundaryLoop || boundaryLoop.length < 3) {
    return [];
  }

  if (!normals) {
    console.warn('[CurveDetection] No normals provided, cannot analyze curvature accurately');
    return [];
  }

  const worldUp = new THREE.Vector3(0, 1, 0);
  const analysis = [];

  for (let i = 0; i < boundaryLoop.length; i++) {
    const currIdx = boundaryLoop[i];
    const nextIdx = boundaryLoop[(i + 1) % boundaryLoop.length];

    // Extract 3D positions
    const curr = new THREE.Vector3(
      positions[currIdx * 3],
      positions[currIdx * 3 + 1],
      positions[currIdx * 3 + 2]
    );
    const next = new THREE.Vector3(
      positions[nextIdx * 3],
      positions[nextIdx * 3 + 1],
      positions[nextIdx * 3 + 2]
    );

    // Get face normal (Vn) - surface normal at current vertex
    const vn = new THREE.Vector3(
      normals[currIdx * 3],
      normals[currIdx * 3 + 1],
      normals[currIdx * 3 + 2]
    ).normalize();

    // Get edge direction (Vd) - direction from current to next vertex
    const vd = new THREE.Vector3()
      .subVectors(next, curr)
      .normalize();

    // Cross product: Vn × Vd gives direction perpendicular to both
    // This tells us which way the edge "faces" relative to the surface
    const edgeFacing = new THREE.Vector3().crossVectors(vn, vd);
    const edgeFacingMagnitude = edgeFacing.length();

    if (edgeFacingMagnitude > 1e-6) {
      edgeFacing.normalize();
    }

    // Dot product with world up vector
    // Positive = edge faces outward/upward (good for projection)
    // Negative = edge faces inward/downward (concave/problematic)
    // Near zero = edge is vertical or tangent (problematic)
    const upwardScore = edgeFacing.dot(worldUp);

    // Also check if edge itself is vertical (as secondary metric)
    const edgeSlope = vd.dot(worldUp);

    analysis.push({
      vertexIndex: currIdx,
      upwardScore,
      edgeSlope,
      edgeFacingMagnitude,
      isProblematic: false // Will be set by detection function
    });
  }

  return analysis;
}

/**
 * Detect problematic curve segments using hysteresis thresholding
 * @param {Array} boundaryLoop - Array of vertex indices forming the boundary
 * @param {Float32Array} positions - Position attribute array
 * @param {Float32Array} normals - Normal attribute array
 * @param {Object} options - Detection options
 * @param {number} options.enterThreshold - Threshold to start detecting curve (default: 0.3)
 * @param {number} options.exitThreshold - Threshold to stop detecting curve (default: 0.6)
 * @param {number} options.minSegmentLength - Minimum vertices in a segment (default: 3)
 * @returns {Array} Array of segment objects { startIdx, endIdx, vertices }
 */
export function detectProblematicSegments(boundaryLoop, positions, normals, options = {}) {
  const {
    enterThreshold = 0.3,
    exitThreshold = 0.6,
    minSegmentLength = 3
  } = options;

  const analysis = analyzeBoundaryCurvature(boundaryLoop, positions, normals);
  const segments = [];
  let currentSegment = null;

  for (let i = 0; i < analysis.length; i++) {
    const { upwardScore, vertexIndex } = analysis[i];

    // Check if we should start a new segment
    if (upwardScore < enterThreshold && !currentSegment) {
      currentSegment = {
        startIdx: i,
        vertices: [vertexIndex],
        scores: [upwardScore]
      };
    }
    // Continue existing segment
    else if (currentSegment) {
      currentSegment.vertices.push(vertexIndex);
      currentSegment.scores.push(upwardScore);

      // Check if we should end the segment
      if (upwardScore > exitThreshold) {
        currentSegment.endIdx = i;

        // Only add if segment is long enough
        if (currentSegment.vertices.length >= minSegmentLength) {
          segments.push(currentSegment);
        }

        currentSegment = null;
      }
    }
  }

  // Handle wrap-around case: segment that crosses the loop boundary
  if (currentSegment && segments.length > 0) {
    // Check if the first segment could be continuation of current
    const firstSeg = segments[0];
    if (firstSeg.startIdx === 0 && currentSegment.startIdx < analysis.length - 1) {
      // Merge current segment with first segment
      currentSegment.endIdx = firstSeg.endIdx;
      currentSegment.vertices.push(...firstSeg.vertices);
      currentSegment.scores.push(...firstSeg.scores);

      if (currentSegment.vertices.length >= minSegmentLength) {
        segments[0] = currentSegment;
      } else {
        segments.shift();
      }
    } else if (currentSegment.vertices.length >= minSegmentLength) {
      currentSegment.endIdx = analysis.length - 1;
      segments.push(currentSegment);
    }
  }

  return segments;
}

/**
 * Create a simple triangulated strip connecting curve to straight line replacement
 * Uses a triangle strip approach instead of complex Earcut triangulation
 * @param {Array} curveVertices - Array of vertex indices in the curve segment
 * @param {Float32Array} positions - Position attribute array
 * @returns {Object} { capTriangles, straightVertices, capVertices3D }
 */
export function createCurveCap(curveVertices, positions) {
  if (!curveVertices || curveVertices.length < 3) {
    return null;
  }

  try {
    // Step 1: Extract 3D positions for the curve
    const curve3D = curveVertices.map(idx =>
      new THREE.Vector3(
        positions[idx * 3],
        positions[idx * 3 + 1],
        positions[idx * 3 + 2]
      )
    );

    const startVert = curve3D[0];
    const endVert = curve3D[curve3D.length - 1];

    // Step 2: Create straight line replacement vertices
    // Match the number of vertices in the original curve
    const straightVertices = [];
    for (let i = 0; i < curve3D.length; i++) {
      const t = i / (curve3D.length - 1);
      const interpolated = new THREE.Vector3().lerpVectors(startVert, endVert, t);
      straightVertices.push(interpolated);
    }

    // Step 3: Create simple triangle strip connecting curve to straight line
    // This creates a ruled surface between the two curves
    const capTriangles = [];
    const capVertices3D = [...curve3D, ...straightVertices];

    // Determine average normal to ensure correct face orientation
    const avgNormal = new THREE.Vector3();
    for (let i = 1; i < curve3D.length - 1; i++) {
      const v0 = curve3D[i - 1];
      const v1 = curve3D[i];
      const v2 = curve3D[i + 1];
      const e1 = new THREE.Vector3().subVectors(v1, v0);
      const e2 = new THREE.Vector3().subVectors(v2, v1);
      const cross = new THREE.Vector3().crossVectors(e1, e2);
      avgNormal.add(cross);
    }
    avgNormal.normalize();

    // Create triangle strip
    const n = curve3D.length; // Number of vertices in each curve
    for (let i = 0; i < n - 1; i++) {
      // Indices in capVertices3D array:
      // curve vertices: 0 to n-1
      // straight vertices: n to 2n-1
      const curveIdx = i;
      const curveNextIdx = i + 1;
      const straightIdx = n + i;
      const straightNextIdx = n + i + 1;

      // Triangle 1: [curve[i], curve[i+1], straight[i]]
      const tri1 = [curveIdx, curveNextIdx, straightIdx];

      // Triangle 2: [straight[i], curve[i+1], straight[i+1]]
      const tri2 = [straightIdx, curveNextIdx, straightNextIdx];

      // Check orientation and possibly flip
      const checkTri = (triIndices) => {
        const v0 = capVertices3D[triIndices[0]];
        const v1 = capVertices3D[triIndices[1]];
        const v2 = capVertices3D[triIndices[2]];

        const e1 = new THREE.Vector3().subVectors(v1, v0);
        const e2 = new THREE.Vector3().subVectors(v2, v0);
        const faceNormal = new THREE.Vector3().crossVectors(e1, e2);

        // If face normal opposes average normal, flip winding
        if (faceNormal.dot(avgNormal) < 0) {
          return [triIndices[0], triIndices[2], triIndices[1]]; // Flipped
        }
        return triIndices;
      };

      capTriangles.push(checkTri(tri1));
      capTriangles.push(checkTri(tri2));
    }

    console.log(`[CurveCap] Created ${capTriangles.length} triangles using simple strip method`);

    return {
      capTriangles,
      straightVertices,
      capVertices3D
    };

  } catch (error) {
    console.warn('Error creating curve cap:', error);
    return null;
  }
}

/**
 * Replace a curve segment in the boundary loop with straight vertices
 * @param {Array} boundaryLoop - Original boundary loop (vertex indices)
 * @param {Object} segment - Segment object from detectProblematicSegments
 * @param {Array} newVertexIndices - Array of new vertex indices to replace the segment
 * @returns {Array} Updated boundary loop
 */
export function replaceBoundarySegment(boundaryLoop, segment, newVertexIndices) {
  if (!boundaryLoop || !segment || !newVertexIndices) {
    return boundaryLoop;
  }

  const { startIdx, endIdx } = segment;
  const updatedLoop = [...boundaryLoop];

  // Calculate the number of vertices to replace
  let replaceCount;
  if (endIdx >= startIdx) {
    replaceCount = endIdx - startIdx + 1;
  } else {
    // Wrap-around case
    replaceCount = (boundaryLoop.length - startIdx) + endIdx + 1;
  }

  // Replace the segment
  if (endIdx >= startIdx) {
    // Simple case: no wrap-around
    updatedLoop.splice(startIdx, replaceCount, ...newVertexIndices);
  } else {
    // Wrap-around case: segment crosses loop boundary
    // Remove from startIdx to end
    updatedLoop.splice(startIdx, boundaryLoop.length - startIdx);
    // Remove from start to endIdx
    updatedLoop.splice(0, endIdx + 1);
    // Add new vertices
    updatedLoop.push(...newVertexIndices);
  }

  return updatedLoop;
}

/**
 * Fix all problematic curves in a boundary loop
 * @param {THREE.BufferGeometry} geometry - Input geometry
 * @param {Array} boundaryLoop - Boundary loop vertex indices
 * @param {Object} options - Options for curve detection
 * @returns {Object} { fixedGeometry, fixedBoundaryLoop, capInfo }
 */
export function fixProblematicCurves(geometry, boundaryLoop, options = {}) {
  const scene = options.debugScene || null;

  if (!geometry || !boundaryLoop || boundaryLoop.length < 3) {
    return {
      fixedGeometry: geometry,
      fixedBoundaryLoop: boundaryLoop,
      capInfo: []
    };
  }

  const positions = geometry.attributes.position.array;

  // Get or compute normals
  let normals = geometry.attributes.normal ? geometry.attributes.normal.array : null;
  if (!normals) {
    console.log('[CurveDetection] Computing vertex normals for geometry');
    geometry.computeVertexNormals();
    normals = geometry.attributes.normal.array;
  }

  // Detect problematic segments using face normal + edge direction approach
  const segments = detectProblematicSegments(boundaryLoop, positions, normals, options);

  if (segments.length === 0) {
    // No problematic curves, return original
    console.log('[CurveDetection] No problematic curves detected');
    return {
      fixedGeometry: geometry,
      fixedBoundaryLoop: boundaryLoop,
      capInfo: []
    };
  }

  console.log(`[CurveDetection] Found ${segments.length} problematic curve segment(s)`);

  // Visual debugging - show curve segments with thick red lines
  if (DEBUG_ENABLED && scene) {
    clearDebugVisualization(scene);
    visualizeCurveSegments(scene, segments, positions, {
      curveColor: 0xff0000,      // Red for detected curves
      curveLineWidth: 5,          // Thick lines
      startMarkerColor: 0x0000ff, // Blue sphere at start
      endMarkerColor: 0xff00ff,   // Magenta sphere at end
      markerSize: 0.1,
      showMarkers: true
    });
    logSegmentStatistics(segments, positions);
  }

  // Clone the geometry to modify
  const fixedGeometry = geometry.clone();
  const fixedPositions = Array.from(fixedGeometry.attributes.position.array);
  const fixedIndices = fixedGeometry.index
    ? Array.from(fixedGeometry.index.array)
    : [];

  let currentVertexCount = fixedPositions.length / 3;
  let updatedBoundaryLoop = [...boundaryLoop];
  const capInfo = [];

  // Process each segment
  for (const segment of segments) {
    const cap = createCurveCap(segment.vertices, positions);

    if (!cap) {
      console.warn('[CurveDetection] Failed to create cap for segment, skipping');
      continue;
    }

    const { capTriangles, straightVertices, capVertices3D } = cap;

    // Add cap vertices to geometry
    const capVertexStartIdx = currentVertexCount;
    for (const vertex of capVertices3D) {
      fixedPositions.push(vertex.x, vertex.y, vertex.z);
    }
    currentVertexCount += capVertices3D.length;

    // Add cap triangle indices
    for (const [i0, i1, i2] of capTriangles) {
      fixedIndices.push(
        capVertexStartIdx + i0,
        capVertexStartIdx + i1,
        capVertexStartIdx + i2
      );
    }

    // Create new boundary vertex indices for the straight line
    // We'll add these as new vertices to maintain exact positions
    const straightVertexIndices = [];
    const straightVertexStartIdx = currentVertexCount;

    for (const vertex of straightVertices) {
      fixedPositions.push(vertex.x, vertex.y, vertex.z);
      straightVertexIndices.push(currentVertexCount);
      currentVertexCount++;
    }

    // Replace the boundary segment
    updatedBoundaryLoop = replaceBoundarySegment(
      updatedBoundaryLoop,
      segment,
      straightVertexIndices
    );

    capInfo.push({
      originalSegment: segment,
      capVertexCount: capVertices3D.length,
      capTriangleCount: capTriangles.length,
      straightVertexCount: straightVertices.length
    });

    console.log(`[CurveDetection] Created cap with ${capVertices3D.length} vertices, ${capTriangles.length} triangles`);
  }

  // Update geometry with new positions and indices
  fixedGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(fixedPositions, 3)
  );

  if (fixedIndices.length > 0) {
    fixedGeometry.setIndex(fixedIndices);
  }

  // Recompute normals and bounds
  fixedGeometry.computeVertexNormals();
  fixedGeometry.computeBoundingSphere();

  console.log(`[CurveDetection] Fixed geometry: ${currentVertexCount} vertices, ${fixedIndices.length / 3} triangles`);

  return {
    fixedGeometry,
    fixedBoundaryLoop: updatedBoundaryLoop,
    capInfo
  };
}
